import { Box, Button, Group, Image, Paper, Stack, Text } from '@mantine/core'
import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { IconPhoto, IconTrash } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'

import { FileUpload } from '@/components/FileUpload'
import { InputLabel } from '@/components/InputLabel'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { ThumbnailDraft } from '@/screens/ContentModuleFormScreen/useReviewModalState'
import * as commonClasses from '@/styles/componentCommons.css'
import { toBareS3Key } from '@/utils/uploads'

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg']

export interface ResourceThumbnailUploadProps {
  /** The saved thumbnail s3 key from the resource (null if none). */
  existingThumbnailKey: string | null
  /** Local edit state. `unchanged` shows the saved thumbnail; `replaced` shows the new upload; `removed` shows the empty dropzone. */
  draft: ThumbnailDraft
  onUploaded: (s3Key: string) => void
  onRemoved: () => void
  disabled?: boolean
}

export function ResourceThumbnailUpload(props: ResourceThumbnailUploadProps) {
  const { configStore } = useGlobalState()
  const { existingThumbnailKey, draft, onUploaded, onRemoved, disabled } = props

  const localPreviewUrlRef = useRef<string | null>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current)
        localPreviewUrlRef.current = null
      }
    }
  }, [])

  // Drop the local object URL whenever we transition out of the `replaced` state
  // (e.g. saved, removed, or step changed under us).
  useEffect(() => {
    if (draft.kind !== 'replaced' && localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current)
      localPreviewUrlRef.current = null
      setLocalPreviewUrl(null)
    }
  }, [draft.kind])

  const { upload, uploading, uploadProgress } = useFileUpload()

  const showExisting = draft.kind === 'unchanged' && !!existingThumbnailKey
  const previewUrl =
    draft.kind === 'replaced'
      ? localPreviewUrl
      : showExisting
        ? `${configStore.serverBaseUrl}/files/${existingThumbnailKey}`
        : null

  async function handleDrop(files: FileWithPath[]) {
    const file = files[0]
    if (!file) return

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      notifications.show({
        title: 'Invalid File Type',
        message: 'Please upload a PNG or JPEG image.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }

    const result = await upload(file)
    if (!result) return

    const s3Key = toBareS3Key(result.filePath)

    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current)
    }
    const objectUrl = URL.createObjectURL(file)
    localPreviewUrlRef.current = objectUrl
    setLocalPreviewUrl(objectUrl)

    onUploaded(s3Key)
  }

  return (
    <Stack gap="xs" maw={420}>
      <InputLabel label="Thumbnail" />

      {previewUrl ? (
        <Paper withBorder radius="md" p="sm" bg="gray.1">
          <Stack gap="sm">
            <Box
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid var(--mantine-color-gray-3)',
                backgroundColor: 'var(--mantine-color-gray-2)',
              }}>
              <Image src={previewUrl} alt="Thumbnail preview" w="100%" h="100%" fit="cover" />
            </Box>

            <input
              ref={replaceInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) handleDrop([file as FileWithPath])
                e.target.value = ''
              }}
            />

            <Group gap="xs" justify="flex-end" wrap="nowrap">
              <Button
                size="xs"
                variant="default"
                onClick={() => replaceInputRef.current?.click()}
                loading={uploading}
                disabled={disabled || uploading}>
                Replace
              </Button>
              <Button
                size="xs"
                variant="outline"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={onRemoved}
                disabled={disabled || uploading}>
                Remove
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <FileUpload
          accept={ACCEPTED_IMAGE_TYPES}
          maxFiles={1}
          onDrop={handleDrop}
          uploadPhase={uploading ? 'uploading' : 'idle'}
          uploadProgress={uploadProgress}
          disabled={disabled}
          icons={{ idle: IconPhoto }}
          titles={{ idle: 'Drag & drop a thumbnail, or browse' }}
          hints={{ idle: 'PNG or JPG, 1280 × 720 recommended' }}
          primaryActionLabel="Browse"
          actionHidden
          EmptyStateProps={{
            TitleProps: { fz: 'sm', fw: 500 },
            DescriptionProps: { size: 'xs', maw: '100%' },
          }}
        />
      )}

      <Text size="xs" c="dimmed">
        Upload a custom image to control how this media appears across the app.
      </Text>
    </Stack>
  )
}
